/* eslint-disable react-refresh/only-export-components -- route registry:
   lazy components + their preloader are one cohesive export surface; this
   module has no refreshable component state. */
import { lazy } from 'react';

import { catalogStore } from '@/i18n/catalog-store';
import { stripLocalePrefix } from '@/i18n/locale-path';

import type { CatalogGroup } from '@/i18n/catalog-groups';
import type { ComponentType, ReactElement } from 'react';

/**
 * Route-level code splitting registry (launch-readiness B2).
 *
 * The marketing bundle was one eager graph (~371 kB gz — nearly 2× the
 * 200 kB entry budget in `.claude/rules/ui-design.md`): every docs content
 * SOT, trust page, and comparison shipped to a visitor who only sees the
 * landing page. Every non-landing route is now a lazy chunk.
 *
 * **SSG safety**: `renderToString` renders a suspended `lazy` as its
 * fallback, which would prerender empty pages. `preloadableLazy` returns a
 * lazy whose factory resolves **synchronously** once `preload()` has
 * completed (React's lazy supports synchronously-resolving thenables), and
 * `entry-server` top-level-awaits {@link preloadAllRoutes} — so the SSG
 * output always contains the full page HTML.
 *
 * **Hydration**: the prerendered HTML wraps routes in a Suspense boundary;
 * React 18 keeps the server HTML visible while the route chunk loads and
 * hydrates it when ready (selective hydration). Client-side navigations are
 * router transitions, so the previous page stays until the next chunk lands.
 */

interface PreloadableLazy {
  readonly Component: ComponentType;
  readonly preload: () => Promise<void>;
}

function preloadableLazy(
  load: () => Promise<{ default: ComponentType }>,
  catalogGroups: readonly CatalogGroup[],
): PreloadableLazy {
  let loaded: { default: ComponentType } | undefined;
  let inflight: Promise<{ default: ComponentType }> | undefined;
  const start = (): Promise<{ default: ComponentType }> => {
    // The route resolves only when BOTH its code chunk and its catalog groups
    // (ADR-0195) are in — an SPA navigation can never render a page whose
    // strings haven't landed (no key-echo flash). A failed group fetch
    // degrades inside the store (EN baseline → key-echo), never rejects here.
    inflight ??= Promise.all([
      load(),
      catalogStore.ensureGroups(catalogGroups).catch(() => undefined),
    ]).then(([m]) => {
      loaded = m;
      return m;
    });
    return inflight;
  };
  const Lazy = lazy(start);
  // B-AUDIT-1 — the HYDRATION pass must never see a React.lazy element: even
  // preloaded (and even with the stream-prerender's Suspense markers), a lazy
  // child inside the route boundary made production hydrateRoot bail to a
  // recoverable client render on EVERY chrome-bearing page load (#418/#423
  // console errors; zero DOM damage, one wasted re-render). Both entries
  // preload the active route BEFORE rendering, so when `loaded` is set this
  // wrapper renders the resolved component DIRECTLY — no lazy, no suspension,
  // plain hydratable children. Only a post-hydration SPA navigation to a
  // not-yet-loaded route takes the `<Lazy />` branch, suspending into the
  // app's existing Suspense fallback exactly as before.
  function Component(props: Record<string, never>): ReactElement {
    if (loaded !== undefined) {
      const Resolved = loaded.default;
      return <Resolved {...props} />;
    }
    return <Lazy {...props} />;
  }
  return {
    Component,
    preload: async () => {
      await start();
    },
  };
}

/* Named-export adapters: pages export named components; lazy wants default. */
const R = {
  features: preloadableLazy(
    () => import('./pages/features').then((m) => ({ default: m.Features })),
    ['pages'],
  ),
  howItWorks: preloadableLazy(
    () => import('./pages/how-it-works').then((m) => ({ default: m.HowItWorks })),
    ['pages'],
  ),
  about: preloadableLazy(
    () => import('./pages/about').then((m) => ({ default: m.About })),
    ['pages'],
  ),
  pricing: preloadableLazy(
    () => import('./pages/pricing').then((m) => ({ default: m.Pricing })),
    ['pages'],
  ),
  pricingHistory: preloadableLazy(
    () => import('./pages/pricing').then((m) => ({ default: m.PricingHistory })),
    ['pages'],
  ),
  buildVsBuy: preloadableLazy(
    () => import('./pages/pricing').then((m) => ({ default: m.BuildVsBuy })),
    ['pages'],
  ),
  contact: preloadableLazy(
    () => import('./pages/contact').then((m) => ({ default: m.Contact })),
    ['pages'],
  ),
  benchmarks: preloadableLazy(
    () => import('./pages/benchmarks').then((m) => ({ default: m.Benchmarks })),
    ['pages'],
  ),
  docsIndex: preloadableLazy(
    () => import('./pages/docs').then((m) => ({ default: m.DocsIndex })),
    ['docs'],
  ),
  docPage: preloadableLazy(
    () => import('./pages/docs').then((m) => ({ default: m.DocPage })),
    ['docs'],
  ),
  onePagersIndex: preloadableLazy(
    () => import('./pages/one-pagers').then((m) => ({ default: m.OnePagersIndex })),
    ['pages'],
  ),
  onePagerPage: preloadableLazy(
    () => import('./pages/one-pagers').then((m) => ({ default: m.OnePagerPage })),
    ['pages'],
  ),
  vsIndex: preloadableLazy(
    () => import('./pages/vs').then((m) => ({ default: m.VsIndex })),
    ['pages'],
  ),
  vsPage: preloadableLazy(
    () => import('./pages/vs').then((m) => ({ default: m.VsPage })),
    ['pages'],
  ),
  status: preloadableLazy(
    () => import('./pages/status').then((m) => ({ default: m.Status })),
    ['pages'],
  ),
  trustHub: preloadableLazy(
    () => import('./pages/trust').then((m) => ({ default: m.TrustHub })),
    ['pages'],
  ),
  subprocessors: preloadableLazy(
    () => import('./pages/trust').then((m) => ({ default: m.Subprocessors })),
    ['pages'],
  ),
  compliance: preloadableLazy(
    () => import('./pages/trust').then((m) => ({ default: m.Compliance })),
    ['pages'],
  ),
  security: preloadableLazy(
    () => import('./pages/trust').then((m) => ({ default: m.Security })),
    ['pages'],
  ),
  disclosure: preloadableLazy(
    () => import('./pages/trust').then((m) => ({ default: m.Disclosure })),
    ['pages'],
  ),
  accessibility: preloadableLazy(
    () => import('./pages/trust').then((m) => ({ default: m.Accessibility })),
    ['pages'],
  ),
  questionnaire: preloadableLazy(
    () => import('./pages/trust').then((m) => ({ default: m.Questionnaire })),
    ['pages'],
  ),
  privacy: preloadableLazy(
    () => import('./pages/trust').then((m) => ({ default: m.Privacy })),
    ['pages'],
  ),
  aiCompliance: preloadableLazy(
    () => import('./pages/trust').then((m) => ({ default: m.AiCompliance })),
    ['pages'],
  ),
  legal: preloadableLazy(
    () => import('./pages/trust').then((m) => ({ default: m.Legal })),
    ['pages'],
  ),
  dpa: preloadableLazy(() => import('./pages/trust').then((m) => ({ default: m.Dpa })), ['pages']),
  terms: preloadableLazy(
    () => import('./pages/trust').then((m) => ({ default: m.Terms })),
    ['pages'],
  ),
  cookies: preloadableLazy(
    () => import('./pages/trust').then((m) => ({ default: m.Cookies })),
    ['pages'],
  ),
  refunds: preloadableLazy(
    () => import('./pages/trust').then((m) => ({ default: m.Refunds })),
    ['pages'],
  ),
} as const;

export const LAZY_ROUTES: Record<keyof typeof R, ComponentType> = Object.fromEntries(
  Object.entries(R).map(([key, value]) => [key, value.Component]),
) as unknown as Record<keyof typeof R, ComponentType>;

/**
 * Resolve the preload for the route a given pathname will render — awaited by
 * `entry-client` BEFORE `hydrateRoot`. `renderToString` emits no Suspense
 * boundary markers, so a lazy that suspends DURING hydration cannot keep the
 * server HTML (React #418/#423 + a fallback flash); with the active route's
 * chunk resolved up front, hydration never suspends.
 */
export function preloadForPath(pathname: string): Promise<void> {
  // Per-locale URLs (ADR-0133): strip the `/es`-style prefix so the same chunk
  // map serves `/features` and `/fr/features` alike. English is unprefixed.
  const enPath = stripLocalePrefix(pathname);
  if (/^\/docs(\/|$)/.test(enPath)) {
    return Promise.all([R.docsIndex.preload(), R.docPage.preload()]).then(() => undefined);
  }
  const exact: Record<string, PreloadableLazy | undefined> = {
    '/features': R.features,
    '/how-it-works': R.howItWorks,
    '/about': R.about,
    '/pricing': R.pricing,
    '/pricing/history': R.pricingHistory,
    '/build-vs-buy': R.buildVsBuy,
    '/pricing/build-vs-buy': R.buildVsBuy,
    '/contact': R.contact,
    '/benchmarks': R.benchmarks,
    '/one-pagers': R.onePagersIndex,
    '/vs': R.vsIndex,
    '/status': R.status,
    '/trust': R.trustHub,
    '/trust/compliance': R.compliance,
    '/trust/security': R.security,
    '/trust/accessibility': R.accessibility,
    '/trust/security-questionnaire': R.questionnaire,
    '/trust/privacy': R.privacy,
    '/trust/ai': R.aiCompliance,
    '/trust/dpa': R.dpa,
    '/legal': R.legal,
    '/terms': R.terms,
    '/cookies': R.cookies,
    '/trust/refunds': R.refunds,
  };
  const normalized = enPath.replace(/\/+$/, '') || '/';
  const hit = exact[normalized];
  if (hit) return hit.preload();
  if (normalized.startsWith('/one-pagers/')) return R.onePagerPage.preload();
  if (normalized.startsWith('/vs/')) return R.vsPage.preload();
  return Promise.resolve(); // home + 404 are eager
}

/** Await every route chunk — the SSG prerender path (and test setup). */
export async function preloadAllRoutes(): Promise<void> {
  await Promise.all(Object.values(R).map((entry) => entry.preload()));
}
